import { Component, HostListener, OnInit, ViewChild } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { FilterGroup, FilterOption, reduceFilterGroupToSelectedOptions } from '@app/components/filter/filter.component';
import { AnalyticEvents } from '@app/enums';
import { EmployeeModuleMenuService } from '@app/modules/employees/services/employee-module-menu.service';
import { AnalyticService } from '@app/services';
import { AuthService } from '@app/services/auth.service';
import { NotifyService } from '@app/services/notify.service';
import { OrgChartV2Component } from '@employees/components/org-chart-v2/org-chart-v2.component';
import { buildD3Data } from '@employees/functions/org-chart';
import { TreeNode } from '@employees/interfaces/tree-node.interface';
import { Department } from '@models/company/department.model';
import { Employee } from '@models/employee/employee.model';

@Component({
    templateUrl: './organization-chart-v2.template.html',
    styleUrls: ['./organization-chart-v2.style.scss'],
})
export class OrganizationChartV2View implements OnInit {
    @ViewChild('orgChart') orgChart: OrgChartV2Component;
    isLoading = true;
    nodes: TreeNode[] = [
        {
            id: 'root',
            name: 'root',
            parentId: null,
            employee: null,
            avatarId: undefined,
            departmentColour: undefined,
            initials: '',
            position: undefined,
        },
    ];
    zoomLevel = 0.8;
    departmentFilterGroup: FilterGroup = {
        label: 'employees.organizationChart.departments',
        options: [],
    };
    filterGroups: Array<FilterGroup> = [this.departmentFilterGroup];

    togglePhotos = true;
    toggleNames = true;
    togglePositions = true;
    toggleDepartments = true;

    data: Employee[] = null;

    /**
     * Limit number of employees loaded - helps for deving
     */
    private readonly EMPLOYEE_LIMIT: number = 250;

    constructor(
        private auth: AuthService,
        private notify: NotifyService,
        private router: Router,
        private analyticsService: AnalyticService,
        private sanitizer: DomSanitizer,
        public employeeModuleMenuService: EmployeeModuleMenuService
    ) {}

    ngOnInit(): void {
        /**
         * Load departments for filters
         */
        Department.param('company', this.auth.company.id)
            .orderBy('name', 'ASC')
            .all()
            .then(([departments]) => {
                for (const department of departments) {
                    this.departmentFilterGroup.options.push({
                        label: department.name,
                        value: department.id,
                        selected: false,
                    });
                }

                this.loadData();
            });
    }

    @HostListener('mousewheel', ['$event'])
    zoom(event: WheelEvent): void {
        event.preventDefault();
        if (event.deltaY > 0) {
            this.zoomOut();
        }
        if (event.deltaY < 0) {
            this.zoomIn();
        }
    }

    updateChart(): void {
        this.orgChart.render();
    }

    gotToEmployee(employee: Employee): void {
        this.router.navigate(['/', 'employees', 'directory', employee.id]);
    }

    goToAccordionView(): void {
        this.analyticsService.trackEvent(AnalyticEvents.OrgChartEnterListView);
        this.router.navigate(['employees', 'organization', 'accordion']);
    }

    onFilter(): void {
        this.analyticsService.trackEvent(AnalyticEvents.OrgChartAppliedFilter);
        this.loadData();
    }

    expandAll(): void {
        this.orgChart.expandAll();
    }

    collapseAll(): void {
        this.orgChart.collapseAll();
    }

    zoomIn(): void {
        this.orgChart.zoomIn();
    }

    zoomOut(): void {
        this.orgChart.zoomOut();
    }

    download(): void {
        this.analyticsService.trackEvent(AnalyticEvents.OrgChartDownloadImage);
        this.orgChart.download();
    }

    fullscreen(): void {
        this.orgChart.fullscreen();
    }

    private async loadData(): Promise<void> {
        this.resetNodes();
        this.isLoading = true;

        let request = Employee.param('company', this.auth.company.id)
            .limit(this.EMPLOYEE_LIMIT) // Load every employee
            .with('account')
            .with('department')
            .with('job')
            .whereIn('status', ['onLeave', 'active'])
            .orderBy('department_id', 'ASC');

        const selectedDepartmentIds = reduceFilterGroupToSelectedOptions(this.departmentFilterGroup).map(
            (option: FilterOption) => option.value
        );

        if (selectedDepartmentIds.length) {
            request = request.whereIn('departmentId', selectedDepartmentIds);
        }

        await request
            .get()
            .then((response) => this.paginateQuery(response, request))
            .catch(() => this.notify.error('employees.organizationChart.unableToRetrieveEmployees'));
        this.isLoading = false;
        this.orgChart.drawChart();
    }

    /**
     * We limit our per-page response for perf reasons, but we want to show all employees.
     * Paginate through all employees, converting response into NodeTrees with minimum required data so
     * we can safely handle ~1000 employee orgs.
     *
     * TODO: Move this to API on a new endpoint, only retuning minimum data for all employees
     */
    private async paginateQuery([employees, meta], request): Promise<void> {
        this.nodes.push(...buildD3Data(this.sanitizer, employees));

        if (meta?.pagination?.page * meta?.pagination?.perPage <= meta?.pagination?.total) {
            await request
                .page(meta?.pagination?.page + 1)
                .get()
                .then((response) => this.paginateQuery(response, request));
        }
    }

    private resetNodes(): void {
        this.nodes = [
            {
                id: 'root',
                name: 'root',
                parentId: null,
                employee: null,
                avatarId: undefined,
                departmentColour: undefined,
                initials: '',
                position: undefined,
            },
        ];
    }
}
